import { useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import SEO from '@components/SEO'
import KrpanoViewer from '@components/KrpanoViewer'
import BlurHashImage from '@components/BlurHashImage'
import KrpanoCompass from '@components/KrpanoCompass'
import UtilityFloorPlan from '@components/UtilityFloorPlan/UtilityFloorPlan'
import UtilityFloorplanDetail from '@components/UtilityFloorplanDetail/UtilityFloorplanDetail'
import { BLUR_HASH_MAP } from '@constants/BlurHashItems'

// Import floor plans for utilities
import planTang1 from '@assets/images/utilities/plan_tang_1.webp'
import planTang2 from '@assets/images/utilities/plan_tang_2.webp'

import './TienIchPage.css'

// Đường dẫn tới thư mục tour 360 tiện ích (tương đối với public/)
const SCENE_FOLDER  = `${import.meta.env.BASE_URL}360/tienich`
const SCRIPT_SRC    = `${import.meta.env.BASE_URL}360/tienich/tour.js`
const COMPASS_IMGS  = `${import.meta.env.BASE_URL}360/tienich/images`
// Fallback: tự ẩn blur sau 20s nếu krpano bị lỗi không ready
const FALLBACK_MS   = 20_000

// Danh sách scene theo thứ tự hiển thị
const SCENES = [
  { id: 'scene_shophouse', label: 'Lối vào' },
  { id: 'scene_kid',       label: 'Khu vui chơi trẻ em' },
  { id: 'scene_bbq',       label: 'Khu BBQ' },
  { id: 'scene_pool',      label: 'Hồ bơi người lớn' },
]

// Cấu hình toạ độ điểm hotspot cho 2 tầng tiện ích kèm góc lệch radarOffset
// Copy đoạn này và thay thế mảng HOTSPOT_POINTS trong src/pages/TienIchPage/index.jsx
// Copy đoạn này và thay thế mảng HOTSPOT_POINTS trong src/pages/TienIchPage/index.jsx
export const HOTSPOT_POINTS = [
  // Tầng 1
  { id: 'khuon_vien_canh_quan', number: 1, label: 'Khuôn viên cảnh quan', scene: '', x: 25.6, y: 45.5, floor: 1 },
  { id: 'loi_vao', number: 2, label: 'Lối vào', scene: 'scene_shophouse', x: 26.5, y: 26.1, floor: 1, radarOffset: 130 },
  { id: 'kid', number: 3, label: 'Khu vui chơi trẻ em', scene: 'scene_kid', x: 43, y: 55, floor: 1, radarOffset: 0 },
  { id: 'yoga', number: 4, label: 'Yoga', scene: '', x: 53, y: 56, floor: 1 },
  { id: 'khu_the_thao_ngoai_tr', number: 5, label: 'Khu thể thao ngoài trời', scene: '', x: 45, y: 60, floor: 1 },
  { id: 'loi_xuong_ham', number: 6, label: 'Lối xuống hầm', scene: '', x: 77.5, y: 24.8, floor: 1 },
  { id: 'vuon_hoa', number: 7, label: 'Vườn hoa', scene: '', x: 81.2, y: 43.1, floor: 1 },
  { id: 'loi_len_ham', number: 8, label: 'Lối lên hầm', scene: '', x: 77.1, y: 61.4, floor: 1 },
  { id: 'sanh_don_block_a', number: 9, label: 'Sảnh đón Block A', scene: '', x: 38.6, y: 35.7, floor: 1 },
  { id: 'sanh_cho_thang_may_1', number: 10, label: 'Sảnh chờ thang máy', scene: '', x: 37.5, y: 45.5, floor: 1 },
  { id: 'sanh_cho_thang_may_2', number: 10, label: 'Sảnh chờ thang máy', scene: '', x: 71.2, y: 45.3, floor: 1 },
  { id: 'shophouse_1', number: 11, label: 'Khu thương mại', scene: '', x: 32, y: 41.8, floor: 1 },
  { id: 'shophouse_2', number: 11, label: 'Khu thương mại', scene: '', x: 32.1, y: 52.6, floor: 1 },
  { id: 'shophouse_3', number: 11, label: 'Khu thương mại', scene: '', x: 62.6, y: 54, floor: 1 },
  { id: 'shophouse_4', number: 11, label: 'Khu thương mại', scene: '', x: 60, y: 42, floor: 1 },
  { id: 'shophouse_5', number: 11, label: 'Khu thương mại', scene: '', x: 47.6, y: 37.8, floor: 1 },
  { id: 'phong_shcd', number: 12, label: 'Phòng SHCĐ', scene: '', x: 67.1, y: 55.3, floor: 1 },
  { id: 'gym', number: 13, label: 'Gym', scene: '', x: 71.8, y: 53.6, floor: 1 },
  { id: 'nha_tre', number: 14, label: 'Nhà trẻ', scene: '', x: 75.9, y: 43, floor: 1 },
  { id: 'sanh_don_block_b', number: 15, label: 'Sảnh đón Block B', scene: '', x: 71.6, y: 38.1, floor: 1 },

  // Tầng 2
  { id: 'shophouse_f2_1', number: 16, label: 'Khu thương mại', scene: '', x: 32, y: 36, floor: 2 },
  { id: 'shophouse_f2_2', number: 16, label: 'Khu thương mại', scene: '', x: 32, y: 53.2, floor: 2 },
  { id: 'shophouse_f2_3', number: 16, label: 'Khu thương mại', scene: '', x: 44.9, y: 33.2, floor: 2 },
  { id: 'shophouse_f2_4', number: 16, label: 'Khu thương mại', scene: '', x: 48.7, y: 46.9, floor: 2 },
  { id: 'shophouse_f2_5', number: 16, label: 'Khu thương mại', scene: '', x: 61, y: 54.5, floor: 2 },
  { id: 'ho_boi_tre_em', number: 17, label: 'Hồ bơi trẻ em', scene: '', x: 53.2, y: 41.3, floor: 2 },
  { id: 'pool', number: 18, label: 'Hồ bơi người lớn', scene: 'scene_pool', x: 58, y: 36.2, floor: 2, radarOffset: 0 },
  { id: 'khu_ghe_nam', number: 19, label: 'Khu ghế nằm', scene: '', x: 65, y: 39.2, floor: 2 },
  { id: 'bbq', number: 20, label: 'Khu BBQ', scene: 'scene_bbq', x: 68, y: 37.4, floor: 2, radarOffset: 90 },
  { id: 'phong_thay_do', number: 21, label: 'Phòng thay đồ', scene: '', x: 72.2, y: 37.8, floor: 2 },
  { id: 'chieu_phim_ngoai_tr', number: 22, label: 'Khu chiếu phim ngoài trời', scene: '', x: 60.5, y: 46.8, floor: 2 },
  { id: 'vuon_thu_gian', number: 23, label: 'Vườn thư giãn', scene: '', x: 55.8, y: 46.4, floor: 2 },
  { id: 'phong_livestream', number: 24, label: 'Phòng livestream', scene: '', x: 67.3, y: 54.6, floor: 2 },
  { id: 'kid_zone_f2', number: 25, label: 'Kid Zone', scene: '', x: 70.3, y: 54.6, floor: 2 },
  { id: 'thu_vien', number: 26, label: 'Thư viện', scene: '', x: 73.3, y: 54.6, floor: 2 },
  { id: 'nha_tre_f2', number: 27, label: 'Nhà trẻ', scene: '', x: 77.3, y: 41.1, floor: 2 },
]

function TienIchPage() {
  const [searchParams] = useSearchParams()
  // initialScene là hằng số trong vòng đời trang — không bao giờ thay đổi
  // để tránh re-init KrpanoViewer khi chuyển scene nội bộ
  const initialScene = SCENES.find(s => s.id === searchParams.get('startscene'))?.id ?? SCENES[0].id

  const krpanoRef             = useRef(null)
  const readyTimerRef         = useRef(null)
  const sceneSyncTimerRef     = useRef(null)
  const activeSceneRef        = useRef(initialScene)
  const [isReady, setIsReady] = useState(false)
  const [activeScene, setActiveScene] = useState(initialScene)

  // States for tracking Krpano view properties
  const [cameraHeading, setCameraHeading] = useState(0)
  const [cameraFov, setCameraFov] = useState(120)  // Khớp với fov mặc định trong tour.xml
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    activeSceneRef.current = activeScene
  }, [activeScene])

  // Fallback: tự ẩn blur sau 20s nếu krpano bị lỗi không ready
  useLayoutEffect(() => {
    const t = window.setTimeout(() => {
      setIsReady(ready => (ready ? ready : true))
    }, FALLBACK_MS)
    return () => window.clearTimeout(t)
  }, [])

  // Đồng bộ scene 2 chiều:
  // - React -> Krpano: qua goToScene()
  // - Krpano -> React: polling xml.scene từ krpanoRef (ổn định hơn jscall bridge)
  useEffect(() => {
    const validScenes = new Set(SCENES.map((s) => s.id))

    const syncScene = () => {
      const current = krpanoRef.current?.get?.('xml.scene')
      // Chỉ sync các scene ứng dụng quan tâm, tránh scene trung gian khi transition
      if (current && validScenes.has(current) && current !== activeSceneRef.current) {
        setActiveScene(current)
      }
    }

    sceneSyncTimerRef.current = window.setInterval(syncScene, 120)
    syncScene()

    return () => {
      clearTimeout(readyTimerRef.current)
      if (sceneSyncTimerRef.current) {
        clearInterval(sceneSyncTimerRef.current)
        sceneSyncTimerRef.current = null
      }
    }
  }, [])

  // Lắng nghe sự kiện krpanoViewChange để cập nhật heading và fov cho radar
  useEffect(() => {
    const handleViewChange = (e) => {
      const { hlookat, fov } = e.detail
      setCameraHeading(Number(hlookat) || 0)
      setCameraFov(Number(fov) || 120)  // Fallback khớp với fov mặc định trong tour.xml
    }
    window.addEventListener('krpanoViewChange', handleViewChange)
    return () => {
      window.removeEventListener('krpanoViewChange', handleViewChange)
    }
  }, [])

  const handleKrpanoReady = useCallback((krpano) => {
    // Đọc ngay giá trị view hiện tại sau khi krpano sẵn sàng để khởi tạo state đúng
    readyTimerRef.current = window.setTimeout(() => {
      const currentScene = krpano?.get?.('xml.scene')
      if (currentScene) {
        // Chỉ cập nhật activeScene (UI), không cập nhật startScene prop của KrpanoViewer
        setActiveScene(currentScene)
      }
      const initHeading = parseFloat(krpano?.get?.('view.hlookat')) || 0
      const initFov     = parseFloat(krpano?.get?.('view.fov'))     || 120
      setCameraHeading(initHeading)
      setCameraFov(initFov)
      setIsReady(true)
    }, 200)
  }, [])

  // Chuyển scene khi click vào hotspot hoặc chuyển tầng
  // Không cập nhật startScene prop của KrpanoViewer để tránh re-init toàn bộ viewer
  const goToScene = useCallback((sceneId) => {
    if (!sceneId) return
    setActiveScene(sceneId)
    krpanoRef.current?.call?.(`loadscene(${sceneId}, null, MERGE, BLEND(0.5));`)
  }, [])

  return (
    <div className="tienich-page">
      <SEO
        title="Tiện Ích"
        description="Khám phá hệ thống tiện ích đẳng cấp của dự án Fenica qua tour 360 độ."
      />

      {/* ── Krpano viewer ── */}
      <KrpanoViewer
        ref={krpanoRef}
        scriptSrc={SCRIPT_SRC}
        sceneFolder={SCENE_FOLDER}
        startScene={initialScene}
        onReady={handleKrpanoReady}
      />

      {/* ── Blurhash loading overlay ── */}
      <div
        className={`tienich-page__blur-overlay${isReady ? ' tienich-page__blur-overlay--ready' : ''}`}
        aria-hidden="true"
      >
        <BlurHashImage
          hash={BLUR_HASH_MAP.tienich}
          fadeOut={isReady}
        />
      </div>

      {/* ── React Compass ── */}
      <KrpanoCompass
        krpanoRef={krpanoRef}
        imagesBase={COMPASS_IMGS}
        visible={isReady}
      />

      {/* ── Sơ đồ mặt bằng tiện ích thu nhỏ ── */}
      {isReady && (
        <UtilityFloorPlan
          activeScene={activeScene}
          heading={cameraHeading}
          fov={cameraFov}
          points={HOTSPOT_POINTS}
          planTang1={planTang1}
          planTang2={planTang2}
          onPointClick={(point) => goToScene(point.scene)}
          onDetailClick={() => setShowDetail(true)}
        />
      )}

      {/* ── Sơ đồ mặt bằng tiện ích phóng to (Modal Detail) ── */}
      <UtilityFloorplanDetail
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        activeScene={activeScene}
        heading={cameraHeading}
        fov={cameraFov}
        points={HOTSPOT_POINTS}
        planTang1={planTang1}
        planTang2={planTang2}
        onPointClick={(point) => goToScene(point.scene)}
      />
    </div>
  )
}

export default TienIchPage
